'use client'

import { X } from 'lucide-react'
import Image from 'next/image'
import React, { useId, useRef } from 'react'

import { AccessibleDialog } from '@/components/ui/accessible-dialog'
import { cn } from '@/utilities/ui'

export type PolaroidDetailDialogProps = {
  closeLabel: string
  detailText?: string
  image?: string
  imageAlt?: string
  onClose: () => void
  open: boolean
  restoreFocusRef?: React.RefObject<HTMLElement | null>
  title?: string
}

export function PolaroidDetailDialog({
  closeLabel,
  detailText,
  image,
  imageAlt,
  onClose,
  open,
  restoreFocusRef,
  title,
}: PolaroidDetailDialogProps) {
  const titleId = useId()
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const visibleTitle = title?.trim() || undefined
  const accessibleTitle = visibleTitle || imageAlt || 'Polaroid'

  return (
    <AccessibleDialog
      ariaLabel={accessibleTitle}
      contentClassName="flex max-w-4xl flex-col"
      initialFocusRef={closeButtonRef}
      onClose={onClose}
      open={open}
      restoreFocusRef={restoreFocusRef}
    >
      <div className="flex max-h-[calc(100dvh-2rem)] flex-col overflow-hidden rounded-2xl bg-bluetext shadow-xl">
        <div className="flex items-start justify-between gap-4 border-b border-white/10 px-6 py-5 lg:px-8">
          {visibleTitle ? (
            <h2
              className="font-display text-2xl font-semibold leading-tight text-white md:text-3xl"
              id={titleId}
            >
              {visibleTitle}
            </h2>
          ) : null}
          <button
            ref={closeButtonRef}
            aria-label={closeLabel}
            className={cn(
              'inline-flex size-10 shrink-0 items-center justify-center rounded-full border border-white/20 text-white transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50 focus-visible:ring-offset-2 focus-visible:ring-offset-bluetext',
              visibleTitle ? undefined : 'ml-auto',
            )}
            onClick={onClose}
            type="button"
          >
            <X aria-hidden className="size-5" />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-5 lg:px-8 lg:py-6">
          {image ? (
            <div className="relative aspect-[4/5] max-h-[min(70dvh,40rem)] w-full overflow-hidden rounded-xl border border-white/15 bg-black/35">
              <Image
                alt={imageAlt ?? accessibleTitle}
                className="object-contain"
                fill
                sizes="(max-width: 768px) 100vw, 896px"
                src={image}
              />
            </div>
          ) : null}

          {detailText ? (
            <p className="font-outfit text-base leading-6 text-white/90">{detailText}</p>
          ) : null}
        </div>
      </div>
    </AccessibleDialog>
  )
}
